import { useState } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';

const SUB_INDEX_LABELS = {
  corridor_infrastructure: 'Corridor',
  proximity_attractor: 'Proximity',
  incident_signal: 'Incident'
};

/**
 * Strip proprietary weighting values from factor labels for display.
 * Removes ", weighted XX.XX" so the underlying weighting math isn't exposed,
 * while preserving the underwriter-useful data (counts, distances).
 * Belt-and-suspenders with the backend strip — protects even if a label
 * slips through with the weighted figure still present.
 */
function cleanLabel(label) {
  if (!label) return label;
  return label.replace(/,?\s*weighted\s+[\d.]+/gi, '');
}

const SUB_INDEX_COLORS = {
  corridor_infrastructure: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  proximity_attractor: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  incident_signal: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' }
};

/**
 * FactorsTable
 *
 * MONDAY MAY 25 UPDATE: Renders "coverage gap" rows for factors that
 * have not_reported coverage status. These appear as informational rows
 * with no deduction, labeled "Trafficking Incidents Not Reported" — so
 * the underwriter sees that we LOOKED for trafficking codes and they
 * weren't there, rather than the absence being silent.
 */
export default function FactorsTable({ factors }) {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [showAll, setShowAll] = useState(false);

  // Separate scored factors from coverage-gap factors (no deduction)
  const scoredFactors = (factors || []).filter(f => (f.deduction || 0) > 0);
  const gapFactors = (factors || []).filter(f => f.coverage === 'not_reported');

  if (scoredFactors.length === 0 && gapFactors.length === 0) {
    return (
      <div className="text-sm text-gray-400 italic py-4 text-center">
        No contributing factors scored above zero. Score is at or near the maximum (100).
      </div>
    );
  }

  const visible = showAll ? scoredFactors : scoredFactors.slice(0, 8);
  const hiddenCount = scoredFactors.length - 8;

  return (
    <div>
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider w-8"></th>
              <th className="text-left px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Sub-index</th>
              <th className="text-left px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Factor</th>
              <th className="text-center px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Severity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((f, idx) => (
              <FactorRow
                key={`scored-${idx}`}
                factor={f}
                expanded={expandedIdx === idx}
                onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                hasSamples={!!(f.sample_incidents?.length || f.sample_flagged?.length || f.sample_facilities?.length)}
              />
            ))}

            {gapFactors.map((f, idx) => (
              <CoverageGapRow key={`gap-${idx}`} factor={f} />
            ))}
          </tbody>
        </table>
      </div>

      {hiddenCount > 0 && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="text-sm text-teal hover:text-teal-dark font-semibold mt-3"
        >
          Show {hiddenCount} more factor{hiddenCount > 1 ? 's' : ''}
        </button>
      )}
      {showAll && scoredFactors.length > 8 && (
        <button
          onClick={() => setShowAll(false)}
          className="text-sm text-gray-400 hover:text-gray-600 mt-3"
        >
          Show less
        </button>
      )}
    </div>
  );
}

function FactorRow({ factor, expanded, onToggle, hasSamples }) {
  const colors = SUB_INDEX_COLORS[factor.sub_index] || SUB_INDEX_COLORS.corridor_infrastructure;
  const severityClass = factor.severity === 'high' ? 'severity-high'
                      : factor.severity === 'moderate' ? 'severity-moderate'
                      : 'severity-low';

  const samples = factor.sample_incidents || factor.sample_flagged || factor.sample_facilities || [];

  return (
    <>
      <tr
        className={`hover:bg-gray-50 transition cursor-pointer ${expanded ? 'bg-gray-50' : ''}`}
        onClick={hasSamples ? onToggle : undefined}
      >
        <td className="px-4 py-3 text-gray-400">
          {hasSamples && (
            expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          )}
        </td>
        <td className="px-4 py-3">
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${colors.bg} ${colors.text} ${colors.border}`}>
            {SUB_INDEX_LABELS[factor.sub_index] || factor.sub_index}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-navy">
          {cleanLabel(factor.label)}
          {factor.description && (
            <div className="text-xs text-gray-500 mt-0.5 italic">{factor.description}</div>
          )}
        </td>
        <td className="px-4 py-3 text-center">
          <span className={severityClass}>{factor.severity}</span>
        </td>
      </tr>

      {expanded && samples.length > 0 && (
        <tr className="bg-gray-50">
          <td colSpan="4" className="px-8 py-4">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
              {factor.sample_incidents ? 'Sample incidents' :
               factor.sample_flagged ? 'Flagged businesses' :
               'Detected facilities'} ({samples.length})
            </div>
            <ul className="space-y-1.5">
              {samples.map((s, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <div className="flex-1">
                    {factor.sample_incidents ? (
                      <span>
                        <span className="font-semibold text-navy">{s.offense}</span>
                        {s.code && <span className="text-xs text-gray-400 ml-2">{s.code}</span>}
                        {s.date && <span className="text-xs text-gray-400 ml-2">· {new Date(s.date).toLocaleDateString()}</span>}
                        {s.distance_mi != null && <span className="text-xs text-gray-400 ml-2">· {s.distance_mi}mi</span>}
                        {s.direct_trafficking && <span className="severity-high ml-2 text-[10px]">DIRECT TRAFFICKING</span>}
                      </span>
                    ) : (
                      <span>
                        <span className="font-semibold text-navy">{s.name}</span>
                        {s.distance_mi != null && <span className="text-xs text-gray-400 ml-2">· {s.distance_mi}mi</span>}
                        {s.reason && <span className="text-xs text-gray-500 ml-2 italic">{s.reason}</span>}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Coverage-gap row — rendered for factors where the underlying NIBRS codes
 * didn't appear in the data feed for the queried jurisdiction. Displayed
 * differently so the underwriter understands the score did not silently
 * absorb a zero on that factor.
 */
function CoverageGapRow({ factor }) {
  const colors = SUB_INDEX_COLORS[factor.sub_index] || SUB_INDEX_COLORS.incident_signal;

  return (
    <tr className="bg-amber-50/40">
      <td className="px-4 py-3">
        <Info size={14} className="text-amber-500" />
      </td>
      <td className="px-4 py-3">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${colors.bg} ${colors.text} ${colors.border}`}>
          {SUB_INDEX_LABELS[factor.sub_index] || factor.sub_index}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-amber-800">
        <div className="font-medium">Trafficking Incidents Not Reported</div>
        <div className="text-xs text-amber-700 mt-0.5">
          NIBRS codes {(factor.nibrs_codes || []).join(', ')} did not appear in this jurisdiction's data feed.
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold uppercase tracking-wider">
          gap
        </span>
      </td>
    </tr>
  );
}
